import {
  Breadcrumbs,
  LinearProgress,
  Stack,
  Toolbar,
  Typography,
  useTheme,
  LinkProps,
  Link,
  Divider,
} from '@mui/material';
import { useContext, useMemo } from 'react';
import { ThemeContext } from 'context/themeContext';
import {
  useLocation,
  useNavigation,
  useFetchers,
  Link as RouterLink,
} from 'react-router';
import { AppContext } from 'context/appContext';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTranslation } from 'react-i18next';

const NavBar = () => {
  const { state } = useContext(AppContext);
  const { logos } = useContext(ThemeContext);
  const { pathname } = useLocation();
  const { palette } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const fetchers = useFetchers();

  const isLoading =
    navigation.state !== 'idle' || fetchers.some((f) => f.state !== 'idle');

  // Remove the first three segments (empty string and tenant and tenant id)
  const breadcrumbs: Array<{ label: string; to: string }> = useMemo(() => {
    const breadcrumbs: Array<{ label: string; to: string }> = [];
    const routeSegments = pathname.split('/');
    routeSegments.forEach((segment, index) => {
      if (index == 0) {
        return;
      }

      if (index == 1) {
        breadcrumbs.push({
          label: t('home'),
          to: `/${segment}/${state.tenant.id}/activity-feed`,
        });
        return;
      }

      if (index === 3) {
        if (routeSegments[3] === 'command-center') {
          breadcrumbs.push({
            label: t('tabMenu.' + segment),
            to: `/tenant/${state.tenant.id}/${routeSegments[3]}/health`,
          });
          return;
        }

        if (routeSegments[3] === 'rule-config') {
          breadcrumbs.push({
            label: t('tabMenu.' + segment),
            to: `/tenant/${state.tenant.id}/${routeSegments[3]}/violations`,
          });
          return;
        }

        if (['property', 'properties'].includes(routeSegments[3])) {
          breadcrumbs.push({
            label: t('property.label_plural', { ns: 'entities' }),
            to: `/tenant/${state.tenant.id}/properties`,
          });
          return;
        }

        if (['property-group', 'property-groups'].includes(routeSegments[3])) {
          breadcrumbs.push({
            label: t('property_group.label_plural', { ns: 'entities' }),
            to: `/tenant/${state.tenant.id}/property-groups`,
          });
          return;
        }
      }

      if (index === 4) {
        // property, property group
        if (['property', 'property-group'].includes(routeSegments[3])) {
          breadcrumbs.push({
            label: state.entityName,
            to: `/tenant/${state.tenant.id}/${routeSegments[3]}/${segment}`,
          });
          return;
        } else if (isNaN(parseInt(segment))) {
          breadcrumbs.push({
            label: t('tabMenu.' + segment),
            to: `/tenant/${state.tenant.id}/${routeSegments[3]}/${segment}`,
          });
          return;
        }
      }

      if (index === 5 && isNaN(parseInt(segment))) {
        if (routeSegments[5] === 'rule-config') {
          breadcrumbs.push({
            label: t('tabMenu.' + segment),
            to: `/tenant/${state.tenant.id}/${routeSegments[3]}/${routeSegments[4]}/${segment}/rules`,
          });
          return;
        }
        breadcrumbs.push({
          label: t('tabMenu.' + segment),
          to: `/tenant/${state.tenant.id}/${routeSegments[3]}/${routeSegments[4]}/${segment}`,
        });
        return;
      }

      if (isNaN(parseInt(segment))) {
        breadcrumbs.push({
          label: t('tabMenu.' + segment),
          to: `/tenant/${state.tenant.id}/${routeSegments[3]}`,
        });
      }
    });

    return breadcrumbs;
  }, [pathname]);

  return (
    <Toolbar
      sx={{
        position: 'fixed',
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: palette.secondary.main,
        width: '100%',
      }}
    >
      <Stack direction={'column'} flex={1} mr={4}>
        {isLoading ? (
          <LinearProgress
            variant='indeterminate'
            color='primary'
            sx={{
              height: 3,
              mb: 1,
              backgroundColor: 'primary.dark',
            }}
          />
        ) : (
          <Divider
            sx={{
              borderColor: 'primary.main',
              borderTopWidth: 'medium',
              mb: 1,
            }}
          />
        )}

        <Stack direction={'row'} justifyContent={'space-between'}>
          <Breadcrumbs
            aria-label='breadcrumb'
            sx={{ flexGrow: 1, py: 1.5, px: 4 }}
            separator={<NavigateNextIcon fontSize='small' color={'primary'} />}
          >
            {breadcrumbs.map((value, index) => {
              const last = index === breadcrumbs.length - 1;
              const to = value.to;
              return last ? (
                <Typography
                  key={to}
                  sx={{
                    color: palette.secondary.contrastText,
                    textTransform: 'uppercase',
                  }}
                  variant='h4'
                >
                  {value.label}
                </Typography>
              ) : (
                <LinkRouter
                  underline='always'
                  color={palette.secondary.contrastText}
                  to={to}
                  key={to}
                  sx={{ textTransform: 'uppercase' }}
                  variant='h4'
                >
                  {value.label}
                </LinkRouter>
              );
            })}
          </Breadcrumbs>
          <Typography color={palette.secondary.contrastText} variant='h5'>
            {`${state.tenant.name}`}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction={'row'}
        display={'flex'}
        alignItems={'center'}
        sx={{ height: 100 }}
      >
        <img
          style={{ padding: logos.bannerPadding }}
          src={logos.bannerLogoUrl}
          height={'100%'}
          alt={'logo banner'}
          id={'traxero-logo'}
        />
      </Stack>
    </Toolbar>
  );
};

interface LinkRouterProps extends LinkProps {
  to: string;
  replace?: boolean;
}

function LinkRouter(props: LinkRouterProps) {
  return <Link {...props} component={RouterLink as any} />;
}

export default NavBar;
